// ============================================
// Aqua (Watercolor) Post-Process Effect
// ============================================
// Based on KinoAqua by Keijiro Takahashi
// Gradient-oriented blur with edge detection for painterly look

import type { IRenderContext } from '../../core/IRenderContext';
import { 
  PostProcessEffect, 
  type PostProcessContext, 
  type PostProcessParameter 
} from '../PostProcessStack';
import { AQUA_LAYOUT } from '../PostProcessLayouts';
import {
  type PipelineHandle,
  type ShaderHandle,
  type TextureHandle,
  NULL_HANDLE,
} from '../../types';
import { embeddedAssets } from '@engine/core';
import { getCompiledShader } from '../ShaderCache';

/**
 * Overlay blend mode for paper texture
 */
export enum AquaOverlayMode {
  Off = 0,
  Multiply = 1,
  Overlay = 2,
  Screen = 3,
}

/**
 * Aqua (Watercolor) post-processing effect
 * Creates a painterly, watercolor-like appearance
 */
export class AquaEffect extends PostProcessEffect {
  readonly type = 'aqua';
  readonly displayName = 'Watercolor';
  
  // Core parameters
  opacity = 1.0;              // Effect intensity
  interval = 1.0;             // Gradient sampling interval
  blurWidth = 1.5;            // Fill blur width
  blurFrequency = 0.5;        // Blur frequency (0-1, exponentially mapped)
  
  // Edge settings
  edgeColorR = 0.2;
  edgeColorG = 0.2;
  edgeColorB = 0.2;
  edgeContrast = 2.0;         // Edge contrast
  edgeOpacity = 0.8;          // Edge color mix amount
  
  // Fill settings
  fillColorR = 1.0;
  fillColorG = 1.0;
  fillColorB = 1.0;
  fillOpacity = 0.9;          // Fill color mix amount
  hueShift = 0.1;             // Hue variation
  
  // Quality
  iterations = 16;            // Blur iterations (4-32)
  
  // Overlay
  overlayMode = AquaOverlayMode.Off;
  overlayOpacity = 0.3;
  noiseScale = 1.0;
  
  // GPU resources
  private shader: ShaderHandle = NULL_HANDLE;
  private pipelineHDR: PipelineHandle = NULL_HANDLE;
  private pipelineLDR: PipelineHandle = NULL_HANDLE;
  private noiseTexture: TextureHandle = NULL_HANDLE;
  private uniformWriter = AQUA_LAYOUT.createWriter();

  /**
   * The noise texture is fetched asynchronously. Until it lands the stack must treat this effect
   * as inactive — an effect that renders nothing leaves its (cleared) destination black, which
   * blacked out the whole chain / backbuffer while the fetch was in flight.
   */
  override get isReady(): boolean {
    return this.noiseTexture !== NULL_HANDLE;
  }
  
  getParameters(): PostProcessParameter[] {
    return [
      // Core
      { name: 'opacity', type: 'float', min: 0, max: 1, default: 1.0, description: 'Effect intensity' },
      { name: 'interval', type: 'float', min: 0.1, max: 3, default: 1.0, description: 'Gradient sampling interval' },
      { name: 'blurWidth', type: 'float', min: 0.1, max: 5, default: 1.5, description: 'Fill blur width' },
      { name: 'blurFrequency', type: 'float', min: 0, max: 1, default: 0.5, description: 'Blur frequency' },
      
      // Edge
      { name: 'edgeColorR', type: 'float', min: 0, max: 1, default: 0.2, description: 'Edge color R' },
      { name: 'edgeColorG', type: 'float', min: 0, max: 1, default: 0.2, description: 'Edge color G' },
      { name: 'edgeColorB', type: 'float', min: 0, max: 1, default: 0.2, description: 'Edge color B' },
      { name: 'edgeContrast', type: 'float', min: 0.5, max: 4, default: 2.0, description: 'Edge contrast' },
      { name: 'edgeOpacity', type: 'float', min: 0, max: 1, default: 0.8, description: 'Edge opacity' },
      
      // Fill
      { name: 'fillColorR', type: 'float', min: 0, max: 1, default: 1.0, description: 'Fill color R' },
      { name: 'fillColorG', type: 'float', min: 0, max: 1, default: 1.0, description: 'Fill color G' },
      { name: 'fillColorB', type: 'float', min: 0, max: 1, default: 1.0, description: 'Fill color B' },
      { name: 'fillOpacity', type: 'float', min: 0, max: 1, default: 0.9, description: 'Fill opacity' },
      { name: 'hueShift', type: 'float', min: 0, max: 1, default: 0.1, description: 'Hue variation' },
      
      // Quality
      { name: 'iterations', type: 'int', min: 4, max: 32, default: 16, description: 'Blur iterations' },
      
      // Overlay
      { name: 'overlayMode', type: 'int', min: 0, max: 3, default: 0, description: 'Overlay mode (0=off, 1=multiply, 2=overlay, 3=screen)' },
      { name: 'overlayOpacity', type: 'float', min: 0, max: 1, default: 0.3, description: 'Paper texture opacity' },
      { name: 'noiseScale', type: 'float', min: 0.1, max: 5, default: 1.0, description: 'Noise/paper scale' },
    ];
  }
  
  getParameterValue(name: string): number | boolean | number[] {
    switch (name) {
      case 'opacity': return this.opacity;
      case 'interval': return this.interval;
      case 'blurWidth': return this.blurWidth;
      case 'blurFrequency': return this.blurFrequency;
      case 'edgeColorR': return this.edgeColorR;
      case 'edgeColorG': return this.edgeColorG;
      case 'edgeColorB': return this.edgeColorB;
      case 'edgeContrast': return this.edgeContrast;
      case 'edgeOpacity': return this.edgeOpacity;
      case 'fillColorR': return this.fillColorR;
      case 'fillColorG': return this.fillColorG;
      case 'fillColorB': return this.fillColorB;
      case 'fillOpacity': return this.fillOpacity;
      case 'hueShift': return this.hueShift;
      case 'iterations': return this.iterations;
      case 'overlayMode': return this.overlayMode;
      case 'overlayOpacity': return this.overlayOpacity;
      case 'noiseScale': return this.noiseScale;
      default: return 0;
    }
  }
  
  setParameterValue(name: string, value: number | boolean | number[]): void {
    if (typeof value !== 'number') return;
    switch (name) {
      case 'opacity': this.opacity = Math.max(0, Math.min(1, value)); break;
      case 'interval': this.interval = Math.max(0.1, Math.min(3, value)); break;
      case 'blurWidth': this.blurWidth = Math.max(0.1, Math.min(5, value)); break;
      case 'blurFrequency': this.blurFrequency = Math.max(0, Math.min(1, value)); break;
      case 'edgeColorR': this.edgeColorR = Math.max(0, Math.min(1, value)); break;
      case 'edgeColorG': this.edgeColorG = Math.max(0, Math.min(1, value)); break;
      case 'edgeColorB': this.edgeColorB = Math.max(0, Math.min(1, value)); break;
      case 'edgeContrast': this.edgeContrast = Math.max(0.5, Math.min(4, value)); break;
      case 'edgeOpacity': this.edgeOpacity = Math.max(0, Math.min(1, value)); break;
      case 'fillColorR': this.fillColorR = Math.max(0, Math.min(1, value)); break;
      case 'fillColorG': this.fillColorG = Math.max(0, Math.min(1, value)); break;
      case 'fillColorB': this.fillColorB = Math.max(0, Math.min(1, value)); break;
      case 'fillOpacity': this.fillOpacity = Math.max(0, Math.min(1, value)); break;
      case 'hueShift': this.hueShift = Math.max(0, Math.min(1, value)); break;
      case 'iterations': this.iterations = Math.floor(Math.max(4, Math.min(32, value))); break;
      case 'overlayMode': this.overlayMode = Math.floor(Math.max(0, Math.min(3, value))) as AquaOverlayMode; break;
      case 'overlayOpacity': this.overlayOpacity = Math.max(0, Math.min(1, value)); break;
      case 'noiseScale': this.noiseScale = Math.max(0.1, Math.min(5, value)); break;
    }
  }
  
  protected onInitialize(ctx: IRenderContext): void {
    const hdrFormat = 'rgba16float' as const;
    const ldrFormat = ctx.getSwapChainFormat();
    
    const aquaWgsl = getCompiledShader('postprocess/aqua');
    this.shader = ctx.createShader(aquaWgsl, aquaWgsl);
    
    // Create pipelines with 2 textures (source + noise)
    this.pipelineHDR = ctx.createPipeline({
      shader: this.shader,
      vertexBuffers: [],
      colorTargets: [{ format: hdrFormat }],
      primitive: { topology: 'triangle-list', cullMode: 'none', usesTextures: true, textureCount: 2 },
      label: 'aqua-hdr',
    });
    this.pipelineLDR = ctx.createPipeline({
      shader: this.shader,
      vertexBuffers: [],
      colorTargets: [{ format: ldrFormat }],
      primitive: { topology: 'triangle-list', cullMode: 'none', usesTextures: true, textureCount: 2 },
      label: 'aqua-ldr',
    });
    
    // Load noise texture
    this.loadNoiseTexture(ctx);
  }
  
  private async loadNoiseTexture(ctx: IRenderContext): Promise<void> {
    try {
      const url = embeddedAssets.resolve('aqua-noise');
      console.log('[AquaEffect] Loading noise texture from:', url);
      const response = await fetch(url);
      
      if (!response.ok) {
        console.warn('[AquaEffect] Fetch failed:', response.status, response.statusText);
        return;
      }
      
      const blob = await response.blob();
      console.log('[AquaEffect] Blob size:', blob.size, 'type:', blob.type);
      
      // Use same options as VFX textureUtils
      const bitmap = await createImageBitmap(blob, {
        premultiplyAlpha: 'premultiply',
        colorSpaceConversion: 'none',
      });

      // The effect may have been disposed while the fetch was in flight — creating the texture
      // then would leak it (dispose already ran and won't run again for it).
      if (!this._initialized) {
        bitmap.close();
        return;
      }

      this.noiseTexture = ctx.createTextureFromImage(bitmap, {
        label: 'aqua-noise',
      });

      console.log('[AquaEffect] Noise texture loaded:', bitmap.width, 'x', bitmap.height);
      bitmap.close();
    } catch (e) {
      console.warn('[AquaEffect] Failed to load noise texture:', e);
    }
  }
  
  protected onRender(context: PostProcessContext): void {
    const { ctx, source, width, height, intensity, isHdrTarget } = context;
    
    // isReady gates rendering at the stack level, but guard anyway for direct callers.
    if (this.noiseTexture === NULL_HANDLE) {
      return;
    }

    // Exponential mapping for blur frequency (as in original KinoAqua)
    const blurFreqExponential = Math.exp((this.blurFrequency - 0.5) * 6);
    
    this.uniformWriter.setAll({
      // Core
      opacity: this.opacity * intensity,
      interval: this.interval,
      blurWidth: this.blurWidth,
      blurFrequency: blurFreqExponential,
      
      // Edge
      edgeColor: [this.edgeColorR, this.edgeColorG, this.edgeColorB],
      edgeContrast: this.edgeContrast,
      
      // Fill
      fillColor: [this.fillColorR, this.fillColorG, this.fillColorB],
      hueShift: this.hueShift,
      
      // Quality & misc
      iterations: this.iterations,
      edgeOpacity: this.edgeOpacity,
      fillOpacity: this.fillOpacity,
      overlayMode: this.overlayMode,
      
      // Screen info
      aspectRatio: width / height,
      overlayOpacity: this.overlayOpacity,
      noiseScale: this.noiseScale,
      _pad: 0,
    });
    this.fullscreenPass(ctx, isHdrTarget ? this.pipelineHDR : this.pipelineLDR, this.uniformWriter.float32, [
      source,
      this.noiseTexture,
    ]);
  }
  
  protected onDispose(ctx: IRenderContext): void {
    if (this.pipelineHDR !== NULL_HANDLE) ctx.destroyPipeline(this.pipelineHDR);
    if (this.pipelineLDR !== NULL_HANDLE) ctx.destroyPipeline(this.pipelineLDR);
    if (this.shader !== NULL_HANDLE) ctx.destroyShader(this.shader);
    if (this.noiseTexture !== NULL_HANDLE) ctx.destroyTexture(this.noiseTexture);
    this.pipelineHDR = NULL_HANDLE;
    this.pipelineLDR = NULL_HANDLE;
    this.shader = NULL_HANDLE;
    this.noiseTexture = NULL_HANDLE;
  }
}
